/**
 * `workflowScopeAt`: the values a field at one step can bind to, grouped This item (named for the list, such as This stage),
 * Earlier steps, Variables, The Job, Workflow settings and Flags, in the order the binding picker lists them. It reads the validator's own walk (`workflowStepScope`), so the picker offers exactly the
 * references a save accepts: an earlier action only when it ran on every path to the step, the loop item only inside a loop.
 * A value is listed once: a list's item fields only through the loop item, never as Earlier steps rows.
 * An entry naming an entity field (`WorkflowEntityFieldRef`) keeps its declared type here; the editor retypes it from the catalogue.
 */
import { MetadataEntityFieldType } from '../../metadata/entity/decorators/model/metadata.entity.field.types';
import type { JsonSafeMetadata, JsonSafeMetadataOption } from '../surface.engine/json.safe.metadata.zod';
import { FeatureFlagMeta, FeatureFlags } from '../settings/feature.flags';
import { ActivityEntityName } from '../shared/activity.entity.name';
import { getExtensionTriggerOptions } from './agent.capability';
import { ExtensionAgentContextKey, ExtensionKind } from './agent.definition';
import { EXTENSION_AGENT_MANUAL_TRIGGER } from './agent.run.now';
import { ExtensionSettingKey } from './extension.setting';
import { EXTENSION_SETTINGS } from './extension.settings';
import { WorkflowStepType, WorkflowValueSource, type WorkflowActionStep, type WorkflowDefinition, type WorkflowForEachStep, type WorkflowStep } from './workflow.definition';
import { workflowStepScope } from './workflow.definition.validation';
import type { WorkflowElementOutput, WorkflowEntityFieldRef } from './workflow.element';
import { WORKFLOW_ELEMENTS } from './workflow.elements';
import type { WorkflowScopeEntry, WorkflowScopeGroup } from './workflow.scope';

/** The entity a run's trigger record is, whose fields the `job.<field>` trigger entries hold. */
const TRIGGER_ENTITY = ActivityEntityName.Job;

/**
 * What started a workflow run (`trigger.events`): the workflow's trigger choices, and Run now. Each `value` is exactly what the run
 * reads there; one that differs makes a condition on it never match.
 */
const TRIGGER_EVENT_OPTIONS: JsonSafeMetadataOption[] = [
    ...getExtensionTriggerOptions(ExtensionKind.Workflow, ExtensionAgentContextKey.Job),
    { value: EXTENSION_AGENT_MANUAL_TRIGGER, label: 'Run now' },
];

/** What the loop item is called when its list does not name it. */
const ITEM_TITLE = 'This item';

/** The row naming the whole loop item when its list has no item fields, such as each event of `trigger.events`. */
const WHOLE_ITEM_NAME = 'Value';

/**
 * The run's trigger data a binding may read: the Job view (`RunJob`) and the trigger (`RunTrigger`) the runtime serves. A
 * related record is offered whole: a condition compares it by `_id`, an action input sends its `_id`, merge text prints its name,
 * and the editor shows it as its field does. A value a field of the trigger record holds names that field (`recordField`), so
 * it is typed through the workflow entity catalogue like any other entity field: its picker, entity data strategy and display
 * are the entity board's. Read by `workflowScopeAt`.
 */
export const WORKFLOW_TRIGGER_ENTRIES: WorkflowScopeEntry[] = [
    triggerEntry('job._id', 'Job › Job', { type: MetadataEntityFieldType.Lookup, entity: ActivityEntityName.Job }),
    triggerEntry('job.jobNumber', 'Job › Job number', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.name', 'Job › Name', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.description', 'Job › Description', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.notes', 'Job › Notes', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.customerPO', 'Job › Customer PO', { type: MetadataEntityFieldType.String }),
    // The Job's system status (`SystemWorkOrderStatus`), as the Job's Status field holds it; stages carry tenant statuses instead.
    triggerEntry('job.status', 'Job › Status', { type: MetadataEntityFieldType.Enum }, recordField('status')),
    triggerEntry('job.priority', 'Job › Priority', { type: MetadataEntityFieldType.Lookup, entity: ActivityEntityName.Priority }, recordField('priority')),
    triggerEntry('job.priority.name', 'Job › Priority name', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.type', 'Job › Job type', { type: MetadataEntityFieldType.Lookup, entity: ActivityEntityName.JobType }, recordField('type')),
    triggerEntry('job.type.name', 'Job › Job type name', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.owner', 'Job › Owner', { type: MetadataEntityFieldType.Lookup, entity: ActivityEntityName.JobOwner }, recordField('owner')),
    triggerEntry('job.owner.name', 'Job › Owner name', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.startDate', 'Job › Start date', { type: MetadataEntityFieldType.DateTime }),
    triggerEntry('job.dueDate', 'Job › Due date', { type: MetadataEntityFieldType.DateTime }),
    triggerEntry('job.completedDate', 'Job › Completed date', { type: MetadataEntityFieldType.DateTime }),
    // Tag records: a condition compares a picked tag by id, and text shows their names.
    triggerEntry('job.labels', 'Job › Labels', { type: MetadataEntityFieldType.Tag, arrayOf: MetadataEntityFieldType.Tag, entity: ActivityEntityName.Tag }, recordField('labels')),
    triggerEntry('job.customer', 'Job › Customer', { type: MetadataEntityFieldType.Lookup, entity: ActivityEntityName.Customer }, recordField('customer')),
    triggerEntry('job.customer.name', 'Job › Customer name', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.customer.contactName', 'Job › Customer contact name', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.customer.contactEmail', 'Job › Customer contact email', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.customer.contactMobile', 'Job › Customer contact mobile', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.site', 'Job › Site', { type: MetadataEntityFieldType.Lookup, entity: ActivityEntityName.SitePro }, recordField('site')),
    triggerEntry('job.site.name', 'Job › Site name', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.site.address', 'Job › Site address', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.site.contactName', 'Job › Site contact name', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.site.contactEmail', 'Job › Site contact email', { type: MetadataEntityFieldType.String }),
    triggerEntry('job.site.contactMobile', 'Job › Site contact mobile', { type: MetadataEntityFieldType.String }),
    triggerEntry('trigger.events', 'Event › Events', { type: MetadataEntityFieldType.Enum, arrayOf: MetadataEntityFieldType.Enum, options: TRIGGER_EVENT_OPTIONS }),
    triggerEntry('trigger.changedFields', 'Event › Changed fields', { type: MetadataEntityFieldType.String, arrayOf: MetadataEntityFieldType.String }),
    triggerEntry('trigger.coalescedCount', 'Event › Folded events', { type: MetadataEntityFieldType.Number }),
    triggerEntry('trigger.firstAt', 'Event › First matched', { type: MetadataEntityFieldType.DateTime }),
    triggerEntry('trigger.lastAt', 'Event › Last matched', { type: MetadataEntityFieldType.DateTime }),
    triggerEntry('workflow.name', 'Workflow › Name', { type: MetadataEntityFieldType.String }),
    triggerEntry('run.startedAt', 'Run › Started at', { type: MetadataEntityFieldType.DateTime }),
    { label: 'Built-in › Today / Now', value: { source: WorkflowValueSource.Now }, metadata: { displayName: 'Today / Now', type: MetadataEntityFieldType.DateTime } },
];

/**
 * Each setting a run may read: an object setting by its fields, a scalar setting whole, typed from `EXTENSION_SETTINGS`.
 */
const SETTING_ENTRIES: WorkflowScopeEntry[] = Object.values(ExtensionSettingKey).flatMap(key => {

    const setting = EXTENSION_SETTINGS[key];
    if (!setting.fields.length) {
        return [{ label: `Settings › ${setting.title}`, value: { source: WorkflowValueSource.Setting, key }, metadata: setting.metadata }];
    }
    return setting.fields.map(field => ({
        label: `${setting.title} › ${field.metadata.displayName ?? field.path}`,
        value: { source: WorkflowValueSource.Setting, key, path: field.path },
        metadata: field.metadata,
    }));
});

/** Every feature flag still in use, as true/false; a deprecated flag is left out so a new workflow never comes to depend on it. */
const FLAG_ENTRIES: WorkflowScopeEntry[] = Object.values(FeatureFlags)
    .filter(flag => !FeatureFlagMeta[flag].deprecated)
    .map(flag => ({
        label: `Flags › ${FeatureFlagMeta[flag].label}`,
        value: { source: WorkflowValueSource.Flag, flag },
        metadata: { displayName: FeatureFlagMeta[flag].label, type: MetadataEntityFieldType.Boolean },
    }));

/**
 * What a field of step `stepId` can bind to. Trigger fields are always offered; an earlier action's outputs only when it ran on
 * every path to the step; the current item only inside a For each, typed from the list the loop goes over; every declared
 * variable, typed as declared; settings and flags always. A step the definition does not hold sees variables, trigger,
 * settings and flags. Called by the workflow editor kit for each step panel it opens.
 */
export function workflowScopeAt(definition: WorkflowDefinition, stepId: string): WorkflowScopeGroup[] {

    const scope = workflowStepScope(definition, stepId);
    const actions = actionSteps(definition.steps).filter(step => scope?.actions.has(step.id));
    const itemTitle = scope?.loop ? loopItemTitle(definition, scope.loop) : ITEM_TITLE;
    const groups: WorkflowScopeGroup[] = [
        {
            source: WorkflowValueSource.Item,
            title: itemTitle,
            entries: scope?.loop ? itemEntries(definition, scope.loop, itemTitle) : [],
        },
        {
            source: WorkflowValueSource.Step,
            title: 'Earlier steps',
            entries: actions.flatMap(step => earlierEntries(step, scope?.loop ?? null)),
        },
        {
            source: WorkflowValueSource.Variable,
            title: 'Variables',
            entries: (definition.variables ?? []).map(variable => ({
                label: `Variables › ${variable.name}`,
                value: { source: WorkflowValueSource.Variable, name: variable.name },
                metadata: { ...variable.metadata, displayName: variable.name },
            })),
        },
        {
            source: WorkflowValueSource.Trigger,
            title: 'The Job',
            entries: WORKFLOW_TRIGGER_ENTRIES,
        },
        {
            source: WorkflowValueSource.Setting,
            title: 'Workflow settings',
            entries: SETTING_ENTRIES,
        },
        {
            source: WorkflowValueSource.Flag,
            title: 'Flags',
            entries: FLAG_ENTRIES,
        },
    ];
    return groups.filter(group => group.entries.length);
}

function triggerEntry(path: string, label: string, metadata: WorkflowScopeEntry['metadata'], entityField?: WorkflowEntityFieldRef): WorkflowScopeEntry {

    return {
        label,
        value: { source: WorkflowValueSource.Trigger, path },
        metadata: { displayName: label.split(' › ')[1], ...metadata },
        ...(entityField ? { entityField } : {}),
    };
}

/** The field `field` of the trigger record, which a trigger entry holds. */
function recordField(field: string): WorkflowEntityFieldRef {

    return { entity: TRIGGER_ENTITY, field };
}

/**
 * What Earlier steps lists for one action. A field of a list's items is never listed: outside a For each over the list it has
 * no single value, and inside one the item group already offers it. The list itself is dropped inside a For each over it,
 * where the item group stands in for it; listing both showed every stage field twice in the Data pane.
 */
function earlierEntries(step: WorkflowActionStep, loop: WorkflowForEachStep | null): WorkflowScopeEntry[] {

    const loopItems = loop?.items;
    const loopedPath = loopItems?.source === WorkflowValueSource.Step && loopItems.stepId === step.id ? loopItems.path ?? '' : null;
    return outputEntries(step)
        .filter(entry => !entry.inList)
        .filter(entry => entry.value.source !== WorkflowValueSource.Step || entry.value.path !== loopedPath);
}

/** An action's declared outputs as `steps.<id>.<path>` entries; a field of a list output's items is marked `inList`. */
function outputEntries(step: WorkflowActionStep): WorkflowScopeEntry[] {

    const outputs = WORKFLOW_ELEMENTS[step.action]?.outputs ?? [];
    const title = step.name || WORKFLOW_ELEMENTS[step.action]?.title || step.id;
    return outputs.map(output => ({
        label: `${title} › ${output.metadata.displayName ?? output.path}`,
        value: { source: WorkflowValueSource.Step, stepId: step.id, path: output.path },
        metadata: output.metadata,
        ...(output.entityField ? { entityField: output.entityField } : {}),
        inList: outputs.some(list => list.metadata.arrayOf && output.path.startsWith(`${list.path}.`)) || undefined,
    }));
}

/** What the loop's item is called: the list output's `itemTitle` ("This stage"), else "This item". */
function loopItemTitle(definition: WorkflowDefinition, loop: WorkflowForEachStep): string {

    return listOutputOf(definition, loop)?.itemTitle ?? ITEM_TITLE;
}

/** The action output a loop goes over, when its items come from an action; undefined for a trigger list or a stale reference. */
function listOutputOf(definition: WorkflowDefinition, loop: WorkflowForEachStep): WorkflowElementOutput | undefined {

    const items = loop.items;
    if (items?.source !== WorkflowValueSource.Step) {
        return undefined;
    }
    const source = actionSteps(definition.steps).find(step => step.id === items.stepId);
    return source ? WORKFLOW_ELEMENTS[source.action]?.outputs.find(output => output.path === (items.path ?? '')) : undefined;
}

/**
 * The loop's current item, then each field of it, labelled `title › Field`. A list from an action takes its item fields from
 * that action's outputs under the list's path; a list from the trigger is a list of scalars, so only the whole item is offered.
 */
function itemEntries(definition: WorkflowDefinition, loop: WorkflowForEachStep, title: string): WorkflowScopeEntry[] {

    const items = loop.items;
    if (items?.source === WorkflowValueSource.Step) {
        const source = actionSteps(definition.steps).find(step => step.id === items.stepId);
        const outputs = source ? WORKFLOW_ELEMENTS[source.action]?.outputs ?? [] : [];
        const listPath = items.path ?? '';
        const list = listOutputOf(definition, loop);
        const subEntries = outputs
            .filter(output => output.path.startsWith(`${listPath}.`))
            .map(output => ({
                label: `${title} › ${output.metadata.displayName ?? output.path}`,
                value: { source: WorkflowValueSource.Item as const, path: output.path.slice(listPath.length + 1) },
                metadata: output.metadata,
                ...(output.entityField ? { entityField: output.entityField } : {}),
            }));
        return subEntries.length ? subEntries : [wholeItem(title, list?.metadata.arrayOf, undefined, list?.metadata.entity)];
    }
    if (items?.source === WorkflowValueSource.Trigger) {
        const list = WORKFLOW_TRIGGER_ENTRIES.find(entry => entry.value.source === WorkflowValueSource.Trigger && entry.value.path === items.path)?.metadata;
        return [wholeItem(title, list?.arrayOf, list?.options, list?.entity)];
    }
    return [wholeItem(title, undefined)];
}

/**
 * The whole current item, typed as the list's item type (and its choices or entity) when it is known. Labelled
 * `title › Value` so its row never repeats the group's own name as its only entry.
 */
function wholeItem(title: string, type: string | undefined, options?: JsonSafeMetadata['options'], entity?: string): WorkflowScopeEntry {

    return {
        label: `${title} › ${WHOLE_ITEM_NAME}`,
        value: { source: WorkflowValueSource.Item },
        metadata: {
            displayName: WHOLE_ITEM_NAME,
            type: type ?? MetadataEntityFieldType.Custom,
            ...(options ? { options } : {}),
            ...(entity ? { entity } : {}),
        },
    };
}

/** Every action step in `steps`, nested ones included, in document order. */
function actionSteps(steps: readonly WorkflowStep[]): WorkflowActionStep[] {

    return steps.flatMap(step => {

        switch (step.type) {
            case WorkflowStepType.Action:
                return [step];
            case WorkflowStepType.If:
                return [...actionSteps(step.then ?? []), ...actionSteps(step.else ?? [])];
            case WorkflowStepType.Switch:
                return [...(step.cases ?? []).flatMap(item => actionSteps(item.steps ?? [])), ...actionSteps(step.default ?? [])];
            case WorkflowStepType.ForEach:
                return actionSteps(step.steps ?? []);
            default:
                return [];
        }
    });
}
